import React from 'react';
import {
  BarChart3, MessageSquare, Target, FileText, Shield,
  Users, Settings, LogOut, User, ChevronRight, Bot,
  Activity, ClipboardCheck
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useClient } from '../contexts/ClientContext';
import { useAuth } from '../contexts/AuthContext';
import LanguageSelector from './LanguageSelector';
import FloatingParticles from './FloatingParticles';

const Sidebar = ({ activePanel, setActivePanel }) => {
  const { t } = useLanguage();
  const { clientConfig, isAdmin, getClientDisplayName, getClientLogo } = useClient();
  const { logout, client } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  // Obtener secciones habilitadas del cliente activo
  const getEnabledSections = () => {
    if (!client || !client.enabled_sections) {
      // Por defecto, todas las secciones están habilitadas EXCEPTO agent_analytics
      return {
        'agent_analytics': false,
        analytics: false,
        informes: false,
        campaigns: false,
        conversations: false,
        reports: false,
        osint: false,
        quality_plan: true
      };
    }

    try {
      return JSON.parse(client.enabled_sections);
    } catch (error) {
      console.error('Error parsing enabled_sections:', error);
      return {
        'agent_analytics': false,
        analytics: false,
        informes: false,
        campaigns: false,
        conversations: false,
        reports: false,
        osint: false,
        quality_plan: true
      };
    }
  };

  const enabledSections = getEnabledSections();
  // Usar directamente agent_analytics de la base de datos
  const isAgentAnalyticsEnabled = enabledSections['agent_analytics'] === true;

  const conversationalItems = [
    { id: 'analytics', label: t('nav.analytics'), icon: BarChart3, enabled: enabledSections.analytics === true },
    { id: 'agent_analytics', label: t('nav.agentAnalytics') || 'Analíticas Agente IA', icon: Bot, enabled: isAgentAnalyticsEnabled },
    { id: 'conversations', label: t('nav.conversations'), icon: MessageSquare, enabled: enabledSections.conversations !== false },
    { id: 'informes', label: t('nav.informes') || 'Informes', icon: FileText, enabled: enabledSections.informes !== false },
    { id: 'campaigns', label: t('nav.campaigns'), icon: Target, enabled: enabledSections.campaigns !== false },
    { id: 'reports', label: t('nav.reports'), icon: FileText, enabled: enabledSections.reports !== false },
  ].filter(item => item.enabled);

  const reputationalItems = [
    { id: 'osint', label: t('nav.osint'), icon: Shield, enabled: enabledSections.osint },
    { id: 'quality_plan', label: 'Plan de Calidad', icon: ClipboardCheck, enabled: enabledSections.quality_plan !== false },
  ].filter(item => item.enabled !== false);

  const adminItems = [
    { id: 'clients', label: 'Gestión de Clientes', icon: Users },
    { id: 'settings', label: 'Configuración', icon: Settings },
  ];

  return (
    <aside className="sidebar-modern">
      <FloatingParticles count={25} />

      {/* Header */}
      <div className="sidebar-header-modern">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-container-modern">
              <img
                src={getClientLogo()}
                alt={`${getClientDisplayName()} Logo`}
                className="logo-image-modern"
              />
            </div>
            <div className="brand-info">
              <h1 className="brand-title">
                {getClientDisplayName()}
              </h1>
              {isAdmin && (
                <div className="admin-indicator">
                  <User size={12} />
                  <span>Admin</span>
                </div>
              )}
            </div>
          </div>

          <div className="header-controls">
            <LanguageSelector />
          </div>
        </div>
      </div>

      {/* Navegación */}
      <nav className="sidebar-nav-modern">
        {/* Sección Conversacional */}
        {conversationalItems.length > 0 && (
          <div className="nav-section-modern">
            <div className="section-header">
              <div className="section-icon">
                <Activity size={16} />
              </div>
              <span className="section-title">CONVERSACIONAL</span>
              <div className="section-line"></div>
            </div>
            <div className="nav-items">
              {conversationalItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    className={`nav-item-modern ${activePanel === item.id ? 'active' : ''}`}
                    onClick={() => setActivePanel(item.id)}
                  >
                    <div className="nav-item-background"></div>
                    <div className="nav-item-content">
                      <div className="nav-icon-wrapper">
                        <Icon size={20} className="nav-icon" />
                      </div>
                      <span className="nav-label">{item.label}</span>
                      <ChevronRight size={16} className="nav-arrow" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Sección Reputacional */}
        {reputationalItems.length > 0 && (
          <div className="nav-section-modern">
            <div className="section-header">
              <div className="section-icon">
                <Shield size={16} />
              </div>
              <span className="section-title">MONITORIZACIÓN</span>
              <div className="section-line"></div>
            </div>
            <div className="nav-items">
              {reputationalItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    className={`nav-item-modern ${activePanel === item.id ? 'active' : ''}`}
                    onClick={() => setActivePanel(item.id)}
                  >
                    <div className="nav-item-background"></div>
                    <div className="nav-item-content">
                      <div className="nav-icon-wrapper">
                        <Icon size={20} className="nav-icon" />
                      </div>
                      <span className="nav-label">{item.label}</span>
                      <ChevronRight size={16} className="nav-arrow" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Sección Administración */}
        {isAdmin && (
          <div className="nav-section-modern">
            <div className="section-header">
              <div className="section-icon">
                <Settings size={16} />
              </div>
              <span className="section-title">ADMINISTRACIÓN</span>
              <div className="section-line"></div>
            </div>
            <div className="nav-items">
              {adminItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    className={`nav-item-modern ${activePanel === item.id ? 'active' : ''}`}
                    onClick={() => setActivePanel(item.id)}
                  >
                    <div className="nav-item-background"></div>
                    <div className="nav-item-content">
                      <div className="nav-icon-wrapper">
                        <Icon size={20} className="nav-icon" />
                      </div>
                      <span className="nav-label">{item.label}</span>
                      <ChevronRight size={16} className="nav-arrow" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Footer con Logout */}
      <div className="sidebar-footer-modern">
        <div className="footer-divider"></div>
        <button
          className="logout-btn-modern"
          onClick={handleLogout}
        >
          <div className="logout-content">
            <LogOut size={18} />
            <span>Cerrar Sesión</span>
          </div>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;